import React, { useState, useEffect } from 'react';

const SunGraphic = () => {
  const [isMobile, setIsMobile] = useState(false);

  useEffect(() => {
    const checkMobile = () => setIsMobile(window.innerWidth < 768);
    checkMobile();
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  const rayCount = isMobile ? 8 : 12;
  const shortRayCount = 6;

  return (
    <div className="absolute inset-0 flex items-center justify-center pointer-events-none overflow-hidden">
      {/* Soft glow background - responsive */}
      <div className="absolute w-64 h-64 md:w-96 md:h-96 lg:w-[600px] lg:h-[600px] bg-gradient-radial from-amber-200/30 via-orange-200/20 to-transparent rounded-full blur-3xl animate-pulse-slow"></div>
      
      {/* Outer glow ring */}
      <div 
        className="absolute w-56 h-56 md:w-80 md:h-80 lg:w-[400px] lg:h-[400px] rounded-full"
        style={{
          background: 'radial-gradient(circle, rgba(251,191,36,0.3) 0%, rgba(251,191,36,0.1) 50%, transparent 70%)',
          animation: 'pulse-glow 3s ease-in-out infinite'
        }}
      ></div>
      
      {/* Main sun container - responsive */}
      <div className="relative w-40 h-40 md:w-56 md:h-56 lg:w-72 lg:h-72">
        
        {/* Rotating rays behind logo */}
        <div className="absolute inset-0 animate-spin-slow">
          {[...Array(rayCount)].map((_, index) => {
            const rotation = isMobile ? (index * 45) : (index * 30);
            return (
              <div
                key={index}
                className="absolute w-1 md:w-1.5 bg-gradient-to-t from-transparent via-amber-400/60 to-amber-200/30"
                style={{
                  height: isMobile ? '100px' : '150px',
                  left: '50%',
                  top: isMobile ? '-50px' : '-75px',
                  transformOrigin: isMobile ? '50% 130px' : '50% 187px',
                  transform: `translateX(-50%) rotate(${rotation}deg)`,
                }}
              />
            );
          })}
        </div>
        
        {/* Secondary rays - slower rotation */}
        <div className="absolute inset-0 animate-spin-slower-reverse">
          {[...Array(shortRayCount)].map((_, index) => {
            const rotation = (index * 60) + 15;
            return (
              <div
                key={`short-${index}`}
                className="absolute w-0.5 md:w-1 bg-gradient-to-t from-transparent via-orange-300/50 to-yellow-200/20"
                style={{
                  height: isMobile ? '70px' : '100px',
                  left: '50%',
                  top: isMobile ? '-35px' : '-50px',
                  transformOrigin: isMobile ? '50% 115px' : '50% 162px',
                  transform: `translateX(-50%) rotate(${rotation}deg)`,
                }}
              />
            );
          })}
        </div>
        
        {/* Logo container with relief effect */}
        <div 
          className="absolute inset-4 md:inset-6 lg:inset-8 rounded-full flex items-center justify-center"
          style={{
            background: 'linear-gradient(145deg, #fef3c7, #fde68a)',
            boxShadow: `
              0 0 60px rgba(251, 191, 36, 0.6),
              0 0 100px rgba(251, 191, 36, 0.4),
              0 0 140px rgba(251, 191, 36, 0.2),
              inset 0 -8px 20px rgba(0, 0, 0, 0.1),
              inset 0 8px 20px rgba(255, 255, 255, 0.8),
              0 4px 15px rgba(0, 0, 0, 0.2)
            `,
            animation: 'sun-pulse 4s ease-in-out infinite'
          }}
        >
          {/* Inner highlight for 3D effect */}
          <div 
            className="absolute inset-2 md:inset-3 rounded-full"
            style={{
              background: 'linear-gradient(135deg, rgba(255,255,255,0.4) 0%, transparent 50%, rgba(0,0,0,0.05) 100%)'
            }}
          ></div>
          
          {/* Logo with relief/emboss effect */}
          <img 
            src="/soleil-azur-logo.png" 
            alt="Soleil d'Azur"
            className="w-3/4 h-3/4 object-contain relative z-10"
            style={{
              filter: `
                drop-shadow(2px 2px 0px rgba(255,255,255,0.8))
                drop-shadow(-1px -1px 0px rgba(0,0,0,0.15))
                drop-shadow(0 0 20px rgba(251,191,36,0.5))
              `,
              animation: 'logo-glow 3s ease-in-out infinite'
            }}
          />
        </div>
        
        {/* Light particles orbiting */}
        <div className="absolute inset-0 animate-spin-very-slow">
          {[...Array(5)].map((_, index) => {
            const angle = (index * 72);
            const radius = isMobile ? 90 : 130;
            return (
              <div
                key={`particle-${index}`}
                className="absolute w-2 h-2 md:w-3 md:h-3 rounded-full"
                style={{
                  background: 'radial-gradient(circle, #fef08a 0%, #fbbf24 50%, transparent 100%)',
                  left: '50%',
                  top: '50%',
                  transform: `rotate(${angle}deg) translateY(-${radius}px) translateX(-50%)`,
                  boxShadow: '0 0 10px rgba(251, 191, 36, 0.8)',
                  animation: `twinkle ${2 + index * 0.3}s ease-in-out infinite`
                }}
              />
            );
          })}
        </div>
      </div>
      
      {/* Radial shading overlay for text readability */}
      <div className="absolute inset-0 bg-gradient-radial from-transparent via-transparent to-white/10 pointer-events-none"></div>
      
      {/* Custom keyframes styles */}
      <style jsx="true">{`
        @keyframes sun-pulse {
          0%, 100% { 
            transform: scale(1);
            box-shadow: 
              0 0 60px rgba(251, 191, 36, 0.6),
              0 0 100px rgba(251, 191, 36, 0.4),
              0 0 140px rgba(251, 191, 36, 0.2),
              inset 0 -8px 20px rgba(0, 0, 0, 0.1),
              inset 0 8px 20px rgba(255, 255, 255, 0.8),
              0 4px 15px rgba(0, 0, 0, 0.2);
          }
          50% { 
            transform: scale(1.02);
            box-shadow: 
              0 0 80px rgba(251, 191, 36, 0.7),
              0 0 120px rgba(251, 191, 36, 0.5),
              0 0 160px rgba(251, 191, 36, 0.3),
              inset 0 -8px 20px rgba(0, 0, 0, 0.1),
              inset 0 8px 20px rgba(255, 255, 255, 0.9),
              0 4px 15px rgba(0, 0, 0, 0.2);
          }
        }
        
        @keyframes logo-glow {
          0%, 100% { 
            filter: 
              drop-shadow(2px 2px 0px rgba(255,255,255,0.8))
              drop-shadow(-1px -1px 0px rgba(0,0,0,0.15))
              drop-shadow(0 0 20px rgba(251,191,36,0.5));
          }
          50% { 
            filter: 
              drop-shadow(2px 2px 0px rgba(255,255,255,0.9))
              drop-shadow(-1px -1px 0px rgba(0,0,0,0.15))
              drop-shadow(0 0 35px rgba(251,191,36,0.7));
          }
        }
        
        @keyframes pulse-glow {
          0%, 100% { opacity: 0.8; transform: scale(1); }
          50% { opacity: 1; transform: scale(1.05); }
        }
        
        @keyframes twinkle {
          0%, 100% { opacity: 0.6; transform: rotate(var(--angle)) translateY(var(--radius)) translateX(-50%) scale(1); }
          50% { opacity: 1; transform: rotate(var(--angle)) translateY(var(--radius)) translateX(-50%) scale(1.3); }
        }
      `}</style>
    </div>
  );
};

export default SunGraphic;
